/**
 * AppFolioAlternatives — SEO landing page targeting the "best AppFolio
 * alternatives" query. Comparison table + FAQ + strong CTA to migration.
 * Route: /alternatives/appfolio
 */
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowRight, Star } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";
import { useEffect } from "react";

const CRITERIA = [
  { row: "Starts at (CAD)", cr: "$5 / mo", af: "$1.40 / unit / mo (min $298 / mo)" },
  { row: "Canadian provincial leases (all 13)", cr: true, af: false },
  { row: "CRA T776 + NR4 exports", cr: true, af: false },
  { row: "Rent in CAD via Stripe", cr: true, af: "USD-first, CAD partial" },
  { row: "Zero-Effort Migration guarantee", cr: true, af: false },
  { row: "Tenant portal (branded)", cr: true, af: true },
  { row: "Real support inside 4 business hours", cr: true, af: "Enterprise tier only" },
  { row: "No sales-call gate", cr: true, af: false },
];

const FAQS = [
  { q: "Is AppFolio a good fit for Canadian landlords?",
    a: "AppFolio is a US-first product. It works, but leases, tax exports (T776, NR4), and CAD rent flows all require workarounds or manual data-entry. Central Rentals is built for the Canadian regulatory stack from day one." },
  { q: "How much cheaper is Central Rentals vs AppFolio?",
    a: "AppFolio's public floor is US$298/month + $1.40 per unit. Central Rentals starts at CA$35/month for one unit and scales flat to CA$80/month for enterprise (50+ units, unlimited)." },
  { q: "Can I migrate my AppFolio data to Central Rentals?",
    a: "Yes. Our Zero-Effort Migration includes CSV import + a 45-minute onboarding call with a real human. Up to 20 properties migrated free on any paid plan." },
];

export default function AppFolioAlternatives() {
  usePageTitle("Best AppFolio alternatives (Canada, 2026) — Central Rentals");

  useEffect(() => {
    const id = "appfolio-alt-jsonld";
    document.getElementById(id)?.remove();
    const s = document.createElement("script");
    s.id = id;
    s.type = "application/ld+json";
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    });
    document.head.appendChild(s);
    return () => { document.getElementById(id)?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="appfolio-alternatives-page">
      <PublicHeader light />
      <section className="max-w-5xl mx-auto px-5 py-12 md:py-16">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-700">
            <Star className="w-3.5 h-3.5"/> Buyer&apos;s guide · Feb 2026
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            The best AppFolio alternatives for Canadian landlords
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            AppFolio is powerful — but it&apos;s built for the US market and starts at CA$400+/month. If you own 1–500 units in Canada and want CRA-ready tax exports, provincial leases, and CAD-native rent collection, here&apos;s a shorter shortlist.
          </p>
        </div>

        <div className="mt-10 overflow-x-auto" data-testid="appfolio-compare-wrap">
          <table className="w-full text-sm min-w-[640px]" data-testid="appfolio-compare-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3 font-bold">Criteria</th>
                <th className="text-left px-4 py-3 font-bold text-emerald-700">Central Rentals</th>
                <th className="text-left px-4 py-3 font-bold">AppFolio</th>
              </tr>
            </thead>
            <tbody>
              {CRITERIA.map((c, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-semibold text-slate-800">{c.row}</td>
                  <td className="px-4 py-3">{renderCell(c.cr)}</td>
                  <td className="px-4 py-3">{renderCell(c.af)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-10 max-w-2xl bg-slate-50 border border-slate-200 rounded-2xl p-6">
          <h2 className="font-display text-xl font-extrabold text-slate-900">Bottom line</h2>
          <p className="mt-2 text-slate-700 leading-relaxed">
            AppFolio is a solid enterprise tool if you already run 200+ US doors and can absorb the price floor. For everyone else — a Canadian solo landlord, a 15-door portfolio, a family-office holding two four-plexes — the maths, leases, and support flow all point to Central Rentals.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/pricing" data-testid="appfolio-cta-pricing" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm">
              See pricing <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link to="/migration" data-testid="appfolio-cta-migration" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-slate-200 hover:border-emerald-400 text-slate-800 font-bold text-sm">
              Move me off AppFolio
            </Link>
          </div>
        </div>

        <div className="mt-14 max-w-3xl" data-testid="appfolio-faqs">
          <h2 className="font-display text-2xl font-extrabold">Frequently asked questions</h2>
          <dl className="mt-4 divide-y divide-slate-200">
            {FAQS.map((f, i) => (
              <div key={i} className="py-4" data-testid={`appfolio-faq-${i}`}>
                <dt className="font-bold text-slate-900">{f.q}</dt>
                <dd className="mt-2 text-sm text-slate-700 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </div>
  );
}

function renderCell(v) {
  if (v === true) return <CheckCircle2 className="w-5 h-5 text-emerald-600"/>;
  if (v === false) return <XCircle className="w-5 h-5 text-slate-300"/>;
  return <span className="text-slate-700">{v}</span>;
}
